import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export const INSTRUMENT_OPTIONS = ['metals', 'fx'] as const;
export type HedgeBookInstrument = (typeof INSTRUMENT_OPTIONS)[number];

// Wire values are the raw hedge_request_status Prisma enum names; the labels
// mirror the launchpad's status filter copy.
export const STATUS_OPTIONS = [
  'Pending_Approval',
  'Approved',
  'Changes_Requested',
  'Executed',
  'Cancelled',
  'Rejected',
] as const;
export const STATUS_FILTER_LABELS: Record<(typeof STATUS_OPTIONS)[number], string> = {
  Pending_Approval: 'Pending',
  Approved: 'Approved',
  Changes_Requested: 'Changes requested',
  Executed: 'Executed',
  Cancelled: 'Cancelled',
  Rejected: 'Rejected',
};
export const METAL_OPTIONS = ['CU', 'AL', 'ZN', 'NI', 'PB', 'SN'] as const;

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

export interface HedgeBookFilters {
  instrument: HedgeBookInstrument;
  status: string | undefined;
  metal: string | undefined;
  search: string | undefined;
  page: number;
}

const PARAM_KEYS = ['hb_inst', 'hb_status', 'hb_metal', 'hb_search', 'hb_page'] as const;

function parseOption<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
  if (value === null) return undefined;
  return (allowed as readonly string[]).includes(value) ? (value as T) : undefined;
}

function parseInstrument(value: string | null): HedgeBookInstrument {
  return value === 'fx' ? 'fx' : 'metals';
}

function parsePage(value: string | null): number {
  if (value === null) return 1;
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) return 1;
  return n;
}

function parseSearch(value: string | null): string | undefined {
  if (value === null) return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

function parseFilters(searchParams: URLSearchParams): HedgeBookFilters {
  const instrument = parseInstrument(searchParams.get('hb_inst'));
  return {
    instrument,
    status: parseOption(searchParams.get('hb_status'), STATUS_OPTIONS),
    // The Asset select is hidden on the FX sub-tab, so a URL-crafted
    // hb_metal would silently blank the FX list — ignore it there.
    metal:
      instrument === 'fx' ? undefined : parseOption(searchParams.get('hb_metal'), METAL_OPTIONS),
    search: parseSearch(searchParams.get('hb_search')),
    page: parsePage(searchParams.get('hb_page')),
  };
}

export function useHedgeBookFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: HedgeBookFilters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const update = useCallback(
    (patch: Partial<HedgeBookFilters>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Filter changes (anything except page) reset to page 1 so users
          // don't land on an empty page in the new result set.
          const filterChanged =
            'instrument' in patch || 'status' in patch || 'metal' in patch || 'search' in patch;
          if (filterChanged) next.delete('hb_page');
          if (patch.instrument !== undefined) {
            // 'metals' is the default sub-tab, so it stays out of the URL.
            if (patch.instrument === 'metals') next.delete('hb_inst');
            else next.set('hb_inst', patch.instrument);
          }
          applyParam(next, 'hb_status', patch, 'status');
          applyParam(next, 'hb_metal', patch, 'metal');
          applyParam(next, 'hb_search', patch, 'search');
          if (patch.page !== undefined) {
            if (patch.page <= 1) next.delete('hb_page');
            else next.set('hb_page', String(patch.page));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearAll = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of PARAM_KEYS) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return { filters, update, clearAll };
}

function applyParam(
  out: URLSearchParams,
  key: string,
  patch: Partial<HedgeBookFilters>,
  patchKey: 'status' | 'metal' | 'search',
): void {
  if (!(patchKey in patch)) return;
  const value = patch[patchKey];
  if (value === undefined || value === '') out.delete(key);
  else out.set(key, value);
}

export function isAnyHedgeBookFilterActive(filters: HedgeBookFilters): boolean {
  // The default 'metals' sub-tab does not count as an active filter.
  if (filters.instrument !== 'metals') return true;
  return Boolean(filters.status ?? filters.metal ?? filters.search);
}
